import React from 'react';

interface Props {
  size?: number;
  /** Draws the enclosing square. Off for inline use next to a wordmark. */
  framed?: boolean;
  title?: string;
}

/**
 * The FolioWorth mark: one steady rise through market noise.
 *
 * Design notes, since the constraints are what shaped it:
 *
 * - The chaos is real data-shaped noise, not decoration — jagged strokes that
 *   overshoot and collapse, the way a market actually looks. It sits at low
 *   opacity so it reads as texture rather than competing with the signal.
 * - The rising line is the only accented element and the only smooth one.
 *   Everything else is angular; the contrast is what makes it legible at
 *   16px, where the noise blurs to a grey wash and the diagonal survives.
 * - No letterforms. "FW" told you nothing and looked like a placeholder.
 * - Geometry is on a 32-unit grid so strokes land on whole pixels at 16, 32
 *   and 64 — the three sizes that actually ship (favicon, sidebar, nav).
 */
export const FolioMark: React.FC<Props> = ({ size = 32, framed = true, title }) => (
  <svg
    width={size} height={size} viewBox="0 0 32 32"
    fill="none" xmlns="http://www.w3.org/2000/svg"
    role={title ? 'img' : 'presentation'}
    aria-hidden={title ? undefined : true}
  >
    {title && <title>{title}</title>}

    {framed && (
      <rect
        x="0.75" y="0.75" width="30.5" height="30.5" rx="8"
        fill="var(--glass-2, rgba(255,255,255,0.05))"
        stroke="var(--border, rgba(255,255,255,0.12))"
        strokeWidth="1.5"
      />
    )}

    {/* Noise. Clipped to the frame so nothing escapes the rounded corner. */}
    <g clipPath="url(#folio-clip)" stroke="var(--text-faint, #7c8798)" strokeWidth="1.25"
       strokeLinecap="round" strokeLinejoin="round" fill="none">
      <path d="M4 21 L7 15 L10 24 L13 12 L16 19 L19 9 L22 17 L25 7 L28 14" opacity="0.28" />
      <path d="M4 26 L8 22 L11 27 L15 20 L18 25 L22 18 L26 23 L28 20" opacity="0.18" />
    </g>

    {/* Signal: the one smooth, accented, rising stroke. */}
    <path
      d="M5 24 C 11 23, 15 18, 19 13 S 25 6, 27 6"
      stroke="var(--inflow, #2dd4a7)" strokeWidth="2.75"
      strokeLinecap="round" fill="none"
    />
    {/* Terminal dot: gives the line a destination rather than a fade-out. */}
    <circle cx="27" cy="6" r="2.5" fill="var(--inflow, #2dd4a7)" />

    <defs>
      <clipPath id="folio-clip">
        <rect x="1.5" y="1.5" width="29" height="29" rx="7" />
      </clipPath>
    </defs>
  </svg>
);
